import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import PageHeader from '../components/common/PageHeader';
import apiService from '../services/shared/apiService';

// Real data types from API
interface Patient {
  uid: string;
  displayName: string;
  email: string;
  lastActive?: string;
  needsAttention?: boolean;
  status?: 'active' | 'inactive' | 'needs_attention';
  progress?: number;
  trend?: 'improving' | 'declining' | 'stable';
  emotionalProfile?: string;
  nextSession?: string;
  alerts?: number;
}

interface Alert {
  id: string;
  patientId: string;
  patientName: string;
  type: 'high_stress' | 'missed_session' | 'concerning_pattern' | 'positive_breakthrough';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  timestamp: string;
  resolved: boolean;
}

const TherapistConsole: React.FC = () => {
  const { user } = useAuth();
  const { themeClasses } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'patients' | 'alerts' | 'analytics'>('overview');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

  // Helper functions for styling
  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'text-green-600 bg-green-100';
      case 'Inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };


  // Handle navigation state from other pages
  useEffect(() => {
    if (location.state) {
      const { selectedPatient: navPatient, openMessageModal } = location.state as any;
      if (navPatient) {
        setSelectedPatient(navPatient);
        if (openMessageModal) {
          setIsMessageModalOpen(true);
        } else {
          setIsPatientModalOpen(true);
        }
      }
    }
  }, [location.state]);

  // Load real data from API
  useEffect(() => {
    const loadRealData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch real patients from API
        const patientsResponse = await apiService.getTherapistPatients(user.uid);
        const realPatients: Patient[] = patientsResponse.patients.map(patient => ({
          uid: patient.uid,
          displayName: patient.displayName || 'Unknown',
          email: patient.email,
          lastActive: typeof patient.lastActive === 'string' ? patient.lastActive : 
                     typeof patient.lastActive === 'number' ? new Date(patient.lastActive).toISOString() : undefined,
          needsAttention: (patient as any).needsAttention || false,
          status: (patient as any).needsAttention ? 'needs_attention' : 'active',
          progress: Math.floor(Math.random() * 100), // TODO: Get real progress from API
          trend: Math.random() > 0.5 ? 'improving' : 'stable', // TODO: Get real trend from API
          emotionalProfile: 'Mental Health Support', // TODO: Get real profile from API
          nextSession: 'TBD', // TODO: Get real next session from API
          alerts: (patient as any).needsAttention ? 1 : 0
        }));
        
        setPatients(realPatients);
        
        // For now, generate some sample alerts based on patient data
        const sampleAlerts: Alert[] = realPatients
          .filter(p => p.needsAttention)
          .map((patient, index) => ({
            id: `alert_${index + 1}`,
            patientId: patient.uid,
            patientName: patient.displayName,
            type: 'concerning_pattern' as const,
            severity: 'medium' as const,
            message: `${patient.displayName} may need attention based on recent activity`,
            timestamp: new Date().toISOString(),
            resolved: false
          }));
        
        setAlerts(sampleAlerts);
        
      } catch (err) {
        console.error('Error loading therapist data:', err);
        setError('Failed to load patient data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadRealData();
  }, [user, location.pathname]); // Added location.pathname to trigger refresh on navigation

  if (!user) {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <p>Please log in to access the therapist console.</p>
        </div>
      </PageContainer>
    );
  }

  if (user.role !== 'therapist') {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <p>Access denied. Therapist role required.</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <p className="text-red-500">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </PageContainer>
    );
  }


  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return '↗️';
      case 'declining': return '↘️';
      case 'stable': return '→';
      default: return '→';
    }
  };

  const totalPatients = patients.length;
  const activePatients = patients.filter(p => p.status === 'active').length;
  const patientsNeedingAttention = patients.filter(p => p.status === 'needs_attention').length;
  const unresolvedAlerts = alerts.filter(a => !a.resolved).length;

  // Alert handlers
  const handleViewAlert = (alert: Alert) => {
    setSelectedAlert(alert);
    setIsAlertModalOpen(true);
  };

  const handleResolveAlert = (alertId: string) => {
    setAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === alertId 
          ? { ...alert, resolved: true }
          : alert
      )
    );
  };

  const handleCloseAlertModal = () => {
    setIsAlertModalOpen(false);
    setSelectedAlert(null);
  };

  // Patient handlers

  const handleMessagePatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsMessageModalOpen(true);
  };

  const handleClosePatientModal = () => {
    setIsPatientModalOpen(false);
    setSelectedPatient(null);
  };

  const handleCloseMessageModal = () => {
    setIsMessageModalOpen(false);
    setSelectedPatient(null);
  };

  return (
    <PageContainer>
      <PageHeader 
        title="Therapist Console" 
        emoji="🖥️"
        description="Manage your patients and clinical tools"
      />


      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'patients', label: 'Patients', icon: '👥' },
            { id: 'alerts', label: 'Alerts', icon: '🔔' },
            { id: 'analytics', label: 'Analytics', icon: '📈' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors bg-white ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
              <div className="flex items-center">
                <div className="text-2xl">👥</div>
                <div className="ml-3">
                  <p className={`text-sm ${themeClasses.secondaryText}`}>Total Patients</p>
                  <p className={`text-2xl font-bold ${themeClasses.primaryText}`}>{totalPatients}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
              <div className="flex items-center">
                <div className="text-2xl">✅</div>
                <div className="ml-3">
                  <p className={`text-sm ${themeClasses.secondaryText}`}>Active Patients</p>
                  <p className={`text-2xl font-bold text-green-600`}>{activePatients}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
              <div className="flex items-center">
                <div className="text-2xl">⚠️</div>
                <div className="ml-3">
                  <p className={`text-sm ${themeClasses.secondaryText}`}>Need Attention</p>
                  <p className={`text-2xl font-bold text-red-600`}>{patientsNeedingAttention}</p>
                </div>
              </div>
            </div>
            <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
              <div className="flex items-center">
                <div className="text-2xl">🔔</div>
                <div className="ml-3">
                  <p className={`text-sm ${themeClasses.secondaryText}`}>Unresolved Alerts</p>
                  <p className={`text-2xl font-bold text-orange-600`}>{unresolvedAlerts}</p>
                </div>
              </div>
            </div>
          </div>


          {/* Recent Activity */}
          <PageSection title="Recent Activity" emoji="📋">
            <div className="space-y-3">
              {alerts.slice(0, 3).map(alert => (
                <div key={alert.id} className={`p-3 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{alert.patientName}</p>
                      <p className="text-sm">{alert.message}</p>
                    </div>
                    <span className="text-xs opacity-75">
                      {new Date(alert.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </PageSection>
        </div>
      )}

      {/* Patients Tab */}
      {activeTab === 'patients' && (
        <PageSection title={`My Patients (${totalPatients})`} emoji="👥">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2">Loading patients...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {patients.map(patient => (
                <div
                  key={patient.uid}
                  onClick={() => setSelectedPatient(patient)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                    selectedPatient?.uid === patient.uid
                      ? 'border-blue-500 bg-blue-50'
                      : `${themeClasses.cardBg} ${themeClasses.border}`
                  }`}
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {patient.displayName.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold ${themeClasses.primaryText} truncate`}>{patient.displayName}</h3>
                      <p className={`text-sm ${themeClasses.secondaryText} truncate`}>{patient.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getStatusColor(patient.status || 'active')}`}>
                      {patient.needsAttention ? 'Needs Attention' : (patient.status || 'active').replace('_', ' ')}
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className={themeClasses.secondaryText}>Last Active</span>
                      <span className={themeClasses.primaryText}>
                        {patient.lastActive ? new Date(patient.lastActive).toLocaleDateString() : 'Unknown'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between text-sm">
                      <span className={themeClasses.secondaryText}>Trend</span>
                      <span className="flex items-center">
                        {getTrendIcon(patient.trend || 'stable')} {patient.trend || 'stable'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-2 mt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/patient/${patient.uid}`);
                      }}
                      className="flex-1 px-3 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                    >
                      View Details
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPatient(patient);
                        setIsMessageModalOpen(true);
                      }}
                      className="flex-1 px-3 py-2 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                    >
                      Message
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </PageSection>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <PageSection title={`Alerts (${unresolvedAlerts} unresolved)`} emoji="🔔">
          <div className="space-y-3">
            {alerts.map(alert => (
              <div key={alert.id} className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold">{alert.patientName}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                        {alert.severity.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm mb-2">{alert.message}</p>
                    <p className="text-xs opacity-75">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleViewAlert(alert)}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => handleResolveAlert(alert.id)}
                      className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PageSection>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <PageSection title="Analytics" emoji="📈">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
              <h3 className={`text-lg font-semibold ${themeClasses.primaryText} mb-4`}>Patient Progress Distribution</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>Excellent (80-100%)</span>
                  <span className={themeClasses.primaryText}>1 patient</span>
                </div>
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>Good (60-79%)</span>
                  <span className={themeClasses.primaryText}>1 patient</span>
                </div>
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>Needs Improvement (40-59%)</span>
                  <span className={themeClasses.primaryText}>1 patient</span>
                </div>
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>Requires Attention (0-39%)</span>
                  <span className={themeClasses.primaryText}>1 patient</span>
                </div>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg ${themeClasses.cardBg} ${themeClasses.border}`}>
              <h3 className={`text-lg font-semibold ${themeClasses.primaryText} mb-4`}>Session Trends</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>This Week</span>
                  <span className={themeClasses.primaryText}>8 sessions</span>
                </div>
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>Last Week</span>
                  <span className={themeClasses.primaryText}>6 sessions</span>
                </div>
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>This Month</span>
                  <span className={themeClasses.primaryText}>32 sessions</span>
                </div>
                <div className="flex justify-between">
                  <span className={themeClasses.secondaryText}>Avg. Session Duration</span>
                  <span className={themeClasses.primaryText}>45 min</span>
                </div>
              </div>
            </div>
          </div>
        </PageSection>
      )}

      {/* Alert Detail Modal */}
      {isAlertModalOpen && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Alert Details</h3>
              <button
                onClick={handleCloseAlertModal}
                className="text-gray-600 hover:text-gray-800 text-2xl bg-transparent hover:bg-gray-100 rounded p-1"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Patient Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Patient Information</h4>
                <p className="text-sm text-gray-600">Name: {selectedAlert.patientName}</p>
                <p className="text-sm text-gray-600">Patient ID: {selectedAlert.patientId}</p>
              </div>

              {/* Alert Details */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-gray-900">Alert Type:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedAlert.severity)}`}>
                    {selectedAlert.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-gray-900">Severity:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedAlert.severity)}`}>
                    {selectedAlert.severity.toUpperCase()}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-gray-900">Message:</span>
                  <p className="text-gray-700 mt-1">{selectedAlert.message}</p>
                </div>

                <div>
                  <span className="font-semibold text-gray-900">Timestamp:</span>
                  <p className="text-gray-700 mt-1">
                    {new Date(selectedAlert.timestamp).toLocaleString()}
                  </p>
                </div>

                <div>
                  <span className="font-semibold text-gray-900">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    selectedAlert.resolved 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedAlert.resolved ? 'RESOLVED' : 'UNRESOLVED'}
                  </span>
                </div>
              </div>

              {/* Additional Context */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Recommended Actions</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  {selectedAlert.type === 'high_stress' && (
                    <>
                      <li>• Schedule immediate follow-up session</li>
                      <li>• Review recent chat messages for triggers</li>
                      <li>• Consider crisis intervention protocols</li>
                    </>
                  )}
                  {selectedAlert.type === 'missed_session' && (
                    <>
                      <li>• Contact patient to reschedule</li>
                      <li>• Check for pattern of missed sessions</li>
                      <li>• Assess patient engagement level</li>
                    </>
                  )}
                  {selectedAlert.type === 'concerning_pattern' && (
                    <>
                      <li>• Review patient's recent activity</li>
                      <li>• Analyze behavioral patterns</li>
                      <li>• Consider treatment plan adjustments</li>
                    </>
                  )}
                  {selectedAlert.type === 'positive_breakthrough' && (
                    <>
                      <li>• Acknowledge patient's progress</li>
                      <li>• Document breakthrough in notes</li>
                      <li>• Consider celebrating milestone</li>
                    </>
                  )}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={handleCloseAlertModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                {!selectedAlert.resolved && (
                  <button
                    onClick={() => {
                      handleResolveAlert(selectedAlert.id);
                      handleCloseAlertModal();
                    }}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Mark as Resolved
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patient Detail Modal */}
      {isPatientModalOpen && selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Patient Details</h2>
              <button 
                onClick={handleClosePatientModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-blue-600">
                    {(selectedPatient.displayName || '').split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedPatient.displayName}</h3>
                  <p className="text-gray-600">{selectedPatient.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Status</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedPatient.status || 'Active')}`}>
                    {selectedPatient.status || 'Active'}
                  </span>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Risk Level</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor('Low')}`}>
                    Low Risk
                  </span>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Progress</h4>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${selectedPatient.progress || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{selectedPatient.progress || 0}%</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Last Active</h4>
                  <p className="text-sm text-gray-600">
                    {selectedPatient.lastActive ? new Date(selectedPatient.lastActive).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                <p className="text-sm text-gray-600">
                  {selectedPatient.needsAttention ? 'This patient requires immediate attention and follow-up.' : 'Patient is stable and progressing well.'}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={handleClosePatientModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  handleClosePatientModal();
                  handleMessagePatient(selectedPatient);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {isMessageModalOpen && selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Send Message</h2>
              <button 
                onClick={handleCloseMessageModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">👤</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedPatient.displayName}</h3>
                  <p className="text-sm text-gray-600">{selectedPatient.email}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter message subject..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea 
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type your message here..."
                ></textarea>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={handleCloseMessageModal}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  alert(`Message sent to ${selectedPatient.displayName}!`);
                  handleCloseMessageModal();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default TherapistConsole; 